import { useEffect, useRef, useState } from "react";
import { renderPoster } from "@/lib/renderPoster";
import { PosterContent } from "@/components/PosterCanvas";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Mail, Loader2, Send } from "lucide-react";

const splitEmails = (txt) => txt.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);

const A4_H = Math.round(1280 / Math.SQRT2);

export const SendPosterButton = ({ poster, iconOnly = false, beforeSend }) => {
  const [open, setOpen] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [extra, setExtra] = useState("");
  const [busy, setBusy] = useState(false);
  const [fresh, setFresh] = useState(null);
  const a4Ref = useRef(null);
  const tvRef = useRef(null);
  const [thumb, setThumb] = useState("");
  const isUsc = (fresh || poster).layout === "usc";

  useEffect(() => {
    if (!open || !fresh) return;
    let live = true;
    const t = setTimeout(async () => {
      const node = isUsc ? a4Ref.current : tvRef.current;
      if (!node) return;
      try {
        const canvas = await renderPoster(node, { pixelRatio: 0.4, width: 1280, height: isUsc ? A4_H : 720, backgroundColor: "#0b1a3a", cacheBust: true });
        if (live) setThumb(canvas.toDataURL("image/jpeg", 0.8));
      } catch {
        if (live) setThumb("error");
      }
    }, 300);
    return () => { live = false; clearTimeout(t); };
  }, [open, fresh, isUsc]);

  const onOpen = async (o) => {
    setOpen(o);
    if (!o) return;
    setExtra("");
    setThumb("");
    const [{ data }, { data: list }] = await Promise.all([api.get("/poster-recipients"), api.get("/posters")]);
    setRecipients(data.emails);
    setFresh(beforeSend ? poster : list.find((x) => x.id === poster.id) || poster);
  };

  const upload = async (node, height, kind) => {
    const opts = { pixelRatio: kind === "a4" ? 2.6 : 1.5, width: 1280, height, backgroundColor: "#0b1a3a", cacheBust: true };
    const canvas = await renderPoster(node, opts);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.9));
    const form = new FormData();
    form.append("file", blob, `${kind}.jpg`);
    form.append("kind", kind);
    await api.post(`/posters/${poster.id}/snapshot`, form, { headers: { "Content-Type": "multipart/form-data" } });
  };

  const send = async () => {
    setBusy(true);
    try {
      if (beforeSend) {
        if (!(await beforeSend())) return;
      } else {
        await document.fonts.ready;
        await upload(tvRef.current, 720, "tv");
        await upload(isUsc ? a4Ref.current : tvRef.current, isUsc ? A4_H : 720, "a4");
      }
      const { data } = await api.post(`/posters/${poster.id}/send-mail`, { extra: splitEmails(extra) });
      toast.success(`Affiche envoyée à : ${data.sent_to.join(", ")}`);
      setOpen(false);
    } catch (e) {
      toast.error(e.response ? formatApiErrorDetail(e.response.data?.detail) : "Impossible de préparer l'image de l'affiche (connexion ou chargement des images). Rechargez la page puis réessayez.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpen}>
      <DialogTrigger asChild>
        {iconOnly ? (
          <Button size="icon" variant="secondary" title="Envoyer par mail" data-testid={`send-poster-${poster.id}`}><Mail className="h-4 w-4" /></Button>
        ) : (
          <Button variant="secondary" size="sm" data-testid={`send-poster-${poster.id}`}><Mail className="mr-1 h-4 w-4" /> Envoyer par mail</Button>
        )}
      </DialogTrigger>
      <DialogContent data-testid="send-poster-dialog">
        <DialogHeader>
          <DialogTitle>Envoyer « {poster.name} »</DialogTitle>
          <DialogDescription>L'affiche est jointe en image A4 paysage.</DialogDescription>
        </DialogHeader>
        <p className="text-xs text-muted-foreground" data-testid="send-poster-fresh-note">
          {beforeSend ? "L'affiche est d'abord enregistrée, puis envoyée telle qu'affichée dans l'éditeur." : "L'image est recréée à partir de la dernière version enregistrée de l'affiche au moment de l'envoi."}
        </p>
        {fresh && (
          <div style={{ position: "fixed", left: -20000, top: 0, width: 1280, pointerEvents: "none", zIndex: -1 }} aria-hidden="true">
            <div ref={tvRef} style={{ width: 1280, height: 720 }}><PosterContent data={fresh} editable={false} /></div>
            {isUsc && <div ref={a4Ref} style={{ width: 1280, height: A4_H }}><PosterContent data={fresh} editable={false} height={A4_H} /></div>}
          </div>
        )}
        <div className="overflow-hidden rounded-lg border border-border bg-muted" style={{ aspectRatio: isUsc ? "1.414" : "16 / 9" }} data-testid="send-poster-thumb">
          {thumb && thumb !== "error" ? <img src={thumb} alt="Aperçu de l'image envoyée" className="h-full w-full object-contain" data-testid="send-poster-thumb-img" />
            : <div className="flex h-full items-center justify-center gap-2 text-xs text-muted-foreground">
              {thumb === "error" ? "Aperçu indisponible" : <><Loader2 className="h-4 w-4 animate-spin" /> Préparation de l'aperçu…</>}
            </div>}
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Destinataires enregistrés</Label>
          <p className="text-sm" data-testid="send-poster-recipients">
            {recipients.length ? recipients.join(", ") : <span className="text-muted-foreground">Aucun (à régler dans Administration)</span>}
          </p>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Ajouter une adresse pour cet envoi (facultatif)</Label>
          <Input value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="ex. secretaire@club.be" data-testid="send-poster-extra-input" />
        </div>
        <DialogFooter>
          <Button onClick={send} disabled={busy || !fresh || (!recipients.length && !splitEmails(extra).length)} data-testid="send-poster-confirm">
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />} Envoyer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
